import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { Link } from 'react-router-dom';
import {
  Leaf,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Cloud,
  Database,
  Zap,
  Activity,
  Settings,
  BarChart3,
  Brain,
  Gauge,
  AlertTriangle,
  MapPin,
  Rocket,
  DollarSign,
  ChevronRight,
  Play
} from 'lucide-react';
import { ImageWithFallback } from '../../figma/ImageWithFallback';
import { ProductTestimonials } from '../../shared/ProductTestimonials';
import { greenH2Testimonials } from '../../shared/testimonialData';

const GreenH2: React.FC = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);

  const features = [
    {
      icon: Cloud,
      title: 'Cloud-Native Predictive Maintenance',
      description: ' Continuously ingests OT data from turbines, electrolyzers, compressors and balance-of-plant equipment, using ML models to predict potential issues before they turn into outages.'
    },
    {
      icon: Brain,
      title: 'Digital Twin of Plant Assets',
      description: 'Builds a dynamic digital twin of key systems so teams can visualise health, remaining life and performance under different operating conditions.'
    },
    {
      icon: Database,
      title: 'Health Prediction & Maintenance Scheduling',
      description: 'Ranks assets by risk and impact, then suggests optimal maintenance windows that align with production commitments and resource availability.'
    },
    {
      icon: MapPin,
      title: '	Expansion Scenario Planning',
      description: 'Lets planners test capacity additions, layout options and grid interconnections using real performance data, reducing uncertainty in capex decisions.'
    },
    {
      icon: Gauge,
      title: '	Geospatial & Environmental Intelligence',
      description: 'Overlays plant data with weather, grid and land‑use signals to highlight external risks and location‑specific opportunities.'
    },
    {
      icon: Activity,
      title: '	ROI & Portfolio Dashboards',
      description: 'Provides investors and management with clear views on asset productivity, downtime drivers and expected returns across sites.'
    },
    {
      icon: Settings,
      title: '•Continuous Learning Engine',
      description: 'Improves prediction accuracy over time as more plants, components and failure modes are added to the platform.'
    },
    {
      icon: AlertTriangle,
      title: 'Open Interfaces & Partner Ecosystem',
      description: 'Connects with existing SCADA, CMMS, ERP and planning tools, and is supported by a network of industry and research partners.'
    }
  ];

  const usecases = [
    {
      icon: Cloud,
      title: 'Plant Reliability & Uptime',
      description: ': Predict failures on critical equipment, plan interventions ahead of time and avoid unplanned outages that can derail delivery contracts.',
      features: ['Multi-Cloud Support', 'Auto-Scaling', 'Edge Computing', 'Global Deployment', 'High Availability']
    },
    {
      icon: Brain,
      title: 'O&M Cost Optimisation',
      description: 'Shift from reactive break‑fix maintenance to condition‑based interventions, cutting overtime, emergency call‑outs and spare‑parts waste.',
      features: ['Predictive Analytics', 'Anomaly Detection', 'Pattern Recognition', 'Self-Learning', 'Digital Twins']
    },
    {
      icon: Database,
      title: 'Greenfield & Brownfield Expansion ',
      description: 'Use historical production data, digital twins and geospatial layers to determine where and when to add new capacity or retrofit existing plants.',
      features: ['SCADA Connectivity', 'Sensor Integration', 'Data Lake', 'Real-Time Sync', 'Data Validation']
    },
    {
      icon: MapPin,
      title: 'Portfolio-Level Performance Management',
      description: 'Compare multiple plants on a like‑for‑like basis, identify chronic under‑performers and roll out best practices across the fleet.',
      features: ['Site Analysis', 'Route Optimization', 'Demand Mapping', 'Terrain Modeling', 'Resource Planning']
    },
    {
      icon: Gauge,
      title: 'Investor & Lender Reporting',
      description: 'Generate data‑driven performance narratives for investors, lenders and off‑takers, backing sustainability claims with hard operational evidence.',
      features: ['KPI Dashboards', 'Custom Reports', 'Automated Alerts', 'Data Export', 'Audit Trails']
    }
  ];

  const compliances = [
    {
      icon: TrendingUp,
      title: '	Secure Cloud Architecture',
      description: 'Applies strong encryption in transit and at rest, with hardened cloud environments and network segmentation around critical services.',
    },
    {
      icon: Zap,
      title: '	Tenant & Role-Based Access Control',
      description: 'Separates data by organisation and project, and lets administrators define who can view, edit or export operational and financial information.',
    },
    {
      icon: BarChart3,
      title: '	Audit Trails & Change History',
      description: ' Logs configuration changes, model updates and key user actions to support internal governance and external audits.',
    },
    {
      icon: DollarSign,
      title: '	Standards-Aligned Practices',
      description: ' Implements security and data-handling practices aligned with common energy and industrial frameworks, helping teams meet their compliance obligations.',
    },
    {
      icon: Rocket,
      title: 'Flexible Data Residency Options',
      description: ' Supports deployment choices that respect regional data‑sovereignty rules and customer policies.',
    }
  ];

  const industryPartners = [
    {
      category: '	Lower Downtime, Higher Output',
      description: 'Predictive maintenance and better scheduling mean more hours online and higher utilisation of high‑value assets.',
    },
    {
      category: '	Smarter Capex Timing',
      description: 'Expansion decisions are grounded in real performance data and scenario analysis, helping teams invest at the right time and scale.',
    },
    {
      category: '	Improved Safety & Equipment Life',
      description: 'Spotting problems early reduces the likelihood of catastrophic failures and extends the life of critical components.',
    },
    {
      category: '	Clearer Line of Sight for Investors',
      description: ' Portfolio dashboards and ROI views give investors and lenders more confidence in projected returns and delivery plans.',
    },
    {
      category: '	Future-Ready Operations',
      description: 'Continuous improvement loops, industry partnerships and planned funding keep the platform evolving with the sector—not locked to today’s assumptions.',
    }
  ];

  const futureRoadmap = [
    {
      title: 'Series A Funding - Q1 2026',
      description: 'Planned Series A round to accelerate platform development, expand team, and scale commercial deployments globally.'
    },
    {
      title: 'Global Market Expansion',
      description: 'Strategic rollout across key hydrogen markets in Europe, Asia-Pacific, and North America with local partnerships.'
    },
    {
      title: 'Compliance & Certification',
      description: 'Full certification and compliance with international hydrogen safety and operational standards across all markets.'
    },
    {
      title: 'Advanced AI Innovation',
      description: 'Continuous advancement of ML models with deep learning, digital twins, and autonomous optimization capabilities.'
    }
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div
        style={{ y: backgroundY }}
        className="fixed inset-0 opacity-20"
      >
        <div className="absolute inset-0">
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-6 h-6 bg-green-500 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 7,
                delay: i * 0.6,
                repeat: Infinity,
                repeatType: "loop"
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 neon-glow">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
                <div>
                  <span className="text-sm font-medium text-green-400 uppercase tracking-wide">Cloud-Native Platform</span>
                  <h1 className="text-4xl lg:text-6xl font-bold">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">Green H₂</span>
                  </h1>
                  <p className="text-sm text-muted-foreground italic">Asset Management Platform</p>
                </div>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-muted-foreground mb-8 leading-relaxed text-justify"
              >
                The Green H2 Asset Platform unifies OT sensor data, machine-learning models and geospatial intelligence into a single, cloud-native monitoring and planning environment. It continuously tracks plant health, predicts failures and surfaces the most cost-effective maintenance windows for critical assets.Beyond day‑to‑day operations, the platform also supports long-term expansion planning. Operators, EPC firms and investors can test different build‑out and repowering scenarios against production targets and return expectations, using the same data that runs the plant today.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
                >
                  Request Platform Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300">
                  <Play className="w-5 h-5" />
                  Watch Overview
                </button>
              </motion.div>

              {/* Key Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="grid grid-cols-3 gap-8 hidden"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">70%</div>
                  <div className="text-sm text-muted-foreground">Downtime Cut</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-400">30%</div>
                  <div className="text-sm text-muted-foreground">Life Extended</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">Monitoring</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative group">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-2xl blur-3xl opacity-20"
                />
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                  <ImageWithFallback
                    src="/assets/Products/ai-Products/greenH2.png"
                    alt="Green H2 Platform Dashboard"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Core <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Comprehensive capabilities designed to optimize green hydrogen operations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground text-justify">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Flowchart Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Workflow</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              End-to-end intelligent monitoring and optimization pipeline
            </p>
          </motion.div>

          {/* Hardcoded Flowchart */}
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 relative">
              {/* Step 1: Data Collection */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-green-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Database className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Data Collection</h3>
                  <p className="text-sm text-muted-foreground">Real-time sensor data from electrolyzer stacks, compressors, and storage systems</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-green-400" />
                </div>
              </motion.div>

              {/* Step 2: Cloud Integration */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Cloud className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Cloud Integration</h3>
                  <p className="text-sm text-muted-foreground">Secure data transmission to cloud platform with edge processing</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-emerald-400" />
                </div>
              </motion.div>

              {/* Step 3: AI Analysis */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-teal-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-4 shadow-lg shadow-teal-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">AI Analysis</h3>
                  <p className="text-sm text-muted-foreground">Machine learning models detect anomalies and predict failures</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-teal-400" />
                </div>
              </motion.div>

              {/* Step 4: Preventive Action */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-cyan-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform duration-300">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Preventive Action</h3>
                  <p className="text-sm text-muted-foreground">Automated alerts and maintenance scheduling to prevent failures</p>
                </div>
                {/* Arrow */}
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ChevronRight className="w-6 h-6 text-cyan-400" />
                </div>
              </motion.div>

              {/* Step 5: Optimization */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="glass-strong rounded-xl p-6 relative group hover:border-blue-500/50 transition-all duration-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                    <Settings className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-white">Optimization</h3>
                  <p className="text-sm text-muted-foreground">Continuous improvement of operating conditions and efficiency</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Capabilities Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent"> Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Advanced technology stack designed for enterprise-scale hydrogen operationsThe platform is designed around real problems faced by green hydrogen producers, industrial operators and project developers. Typical ways teams use it include:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {usecases .map((usecase, index) => {
              const Icon = usecase.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl mb-2 text-white">{usecase.title}</h3>
                      <p className="text-sm text-muted-foreground text-justify">{usecase.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {usecase.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground hidden">
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative py-24 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Data Security & <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent"> Compliance</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              Operational data from energy assets is highly sensitive, so the Green H2 Asset Platform is engineered with utility‑grade safeguards. IT and OT teams get the controls they need without slowing down innovation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {compliances.map((compliance, index) => {
              const Icon = compliance.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass rounded-xl p-6 border-l-4 border-green-500/50 hover:border-green-500 transition-all duration-300"
                >
                  <Icon className="w-8 h-8 text-green-400 mb-3" />
                  <h3 className="font-semibold text-lg mb-2 text-white">{compliance.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 text-justify">{compliance.description}</p>

                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Partnerships Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Platform  <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent"> Benefits</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              For leaders building and operating green hydrogen assets, the platform is a way to de‑risk growth. It ties together plant reliability, capex planning and investor confidence into one data-driven picture.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industryPartners.map(( item, index ) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-strong rounded-xl p-6 hover:scale-105 transition-all duration-300"
              >
                <h3 className="font-semibold text-lg mb-4 text-emerald-400">{item.category}</h3>
                <p className="text-sm text-muted-foreground text-justify">{item.description}</p>

              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future-Ready Solutions Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl mb-4">
              Future-Ready <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Solutions</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
              With Series A funding planned for Q1 2026, the platform is positioned to drive innovation and efficiency in the green hydrogen sector.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {futureRoadmap.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-strong rounded-xl p-8 hover:scale-105 transition-all duration-300"
              >
                <h3 className="font-semibold text-xl mb-3 text-teal-400">{item.title}</h3>
                <p className="text-sm text-muted-foreground text-justify">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Funding Highlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-8 border-2 border-teal-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-transparent to-cyan-500/10" />
            <div className="relative z-10 text-center">
              <Rocket className="w-16 h-16 text-teal-400 mx-auto mb-4" />
              <h3 className="text-2xl lg:text-3xl font-semibold mb-3 text-white">Series A Funding - Q1 2026</h3>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6 text-center">
                Join us in revolutionizing green hydrogen operations. Our upcoming Series A round will accelerate platform development, expand our team, and scale commercial deployments globally.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all duration-300"
              >
                Investor Inquiries
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-white/[0.02] hidden">
        <ProductTestimonials 
          testimonials={greenH2Testimonials}
          title="Hydrogen Operations Excellence"
          subtitle="See how organizations optimized plant performance with Green H₂"
          gradientFrom="#10B981"
          gradientVia="#0FFFA0"
          gradientTo="#10B981"
          iconColor="#10B981"
          dotColor="bg-green-500"
          speed={60}
          direction="left"
        />
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-strong rounded-2xl p-12 text-center border border-green-500/30 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-emerald-500/10" />
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl mb-4 text-white">
                Ready to Transform Your <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Hydrogen Operations?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-center">
                Schedule a demo to see how the Green H2 Asset Platform can optimize your plant performance, reduce downtime, and support strategic expansion.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                >
                  Schedule Demo
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl font-semibold border border-green-500/30 hover:border-green-500/50 transition-all duration-300 text-white"
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GreenH2;